export interface FilterLoadFailure {
	cancel: () => void;
	retry: () => void;
}

export interface GridFilterFeedbackState {
	failures: ReadonlyMap<string, FilterLoadFailure>;
	refusal?: { colId: string; operation: "sort" | "filter" };
}

interface FeedbackStore {
	listeners: Set<() => void>;
	state: GridFilterFeedbackState;
}

const stores = new WeakMap<object, FeedbackStore>();
const getStore = (api: object): FeedbackStore => {
	let store = stores.get(api);
	if (!store) {
		store = { listeners: new Set(), state: { failures: new Map() } };
		stores.set(api, store);
	}
	return store;
};

const getGridFilterFeedback = (api: object): GridFilterFeedbackState => getStore(api).state;

const subscribeGridFilterFeedback = (api: object, listener: () => void): (() => void) => {
	const store = getStore(api);
	store.listeners.add(listener);
	return () => {
		store.listeners.delete(listener);
	};
};

const publish = (store: FeedbackStore, state: GridFilterFeedbackState): void => {
	store.state = state;
	for (const listener of store.listeners) listener();
};

const setFilterLoadFailure = (api: object, colId: string, failure?: FilterLoadFailure): void => {
	const store = getStore(api);
	const current = store.state.failures;
	if (failure ? current.get(colId) === failure : !current.has(colId)) return;
	const failures = new Map(current);
	if (failure) failures.set(colId, failure);
	else failures.delete(colId);
	publish(store, { ...store.state, failures });
};

const notifyGridQueryRefusal = (api: object, refusal: NonNullable<GridFilterFeedbackState["refusal"]>): void => {
	const store = getStore(api);
	const current = store.state.refusal;
	if (current?.colId === refusal.colId && current.operation === refusal.operation) return;
	publish(store, { ...store.state, refusal });
};

const clearGridQueryRefusal = (api: object): void => {
	const store = getStore(api);
	if (!store.state.refusal) return;
	publish(store, { ...store.state, refusal: undefined });
};

const gridFilterFeedback = {
	clearGridQueryRefusal,
	getGridFilterFeedback,
	notifyGridQueryRefusal,
	setFilterLoadFailure,
	subscribeGridFilterFeedback
};

export default gridFilterFeedback;
